import { Component, Inject } from "@angular/core";
import { BaseChartDirective } from "ng2-charts";
import { MagicChartBaseComponent } from "./magic-chart-base.component";
import { environment } from "../../../../../environments/environment";
import { NgClass } from "@angular/common";
import { debounceTime, Subscription } from "rxjs";
import { DeckCardEvent } from "@ponder-core/events/deck.events";
import { DeckLayerName } from "@apiserver-rest/ponder-data-client";
import { producesBlack, producesBlue, producesColorless, producesGreen, producesRed, producesWhite } from "@ponder-features/magic/card/magicCardUtils";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { ChartDataLabelType } from "@ponder-shared/charts/chart-base.component";

@Component({
    selector: "magic-mana-produced-pie-chart",
    imports: [BaseChartDirective, NgClass],
    templateUrl: "./magic-mana-produced-pie-chart.component.html"
})
export class MagicManaProducedPieChartComponent extends MagicChartBaseComponent {
    public summary: string = "Insufficient data available.";

    public black: ManaProduction = new ManaProduction("Black", environment.colors.magic.black, 3);
    public white: ManaProduction = new ManaProduction("White", environment.colors.magic.white, 1);
    public red: ManaProduction = new ManaProduction("Red", environment.colors.magic.red, 4);
    public green: ManaProduction = new ManaProduction("Green", environment.colors.magic.green, 5);
    public blue: ManaProduction = new ManaProduction("Blue", environment.colors.magic.blue, 2);
    public colorless: ManaProduction = new ManaProduction("Colorless", environment.colors.magic.colorless, 6);

    private allColors: ManaProduction[] = [ this.black, this.white, this.red, this.green, this.blue, this.colorless ];
    private deckCardSub!: Subscription;

    private mode: ProductionDisplayMode = ProductionDisplayMode.Land;
    private landModifier: number = 1;
    private nonlandModifier: number = 0;

    public override ngOnInit(): void {
        this.dataLabel = "Producers";
        this.dataLabelType = ChartDataLabelType.Text;
        this.largeScreenAspectRatio = 1;
        this.smallScreenAspectRatio = 1;
        this.xGridLineVisible = false;
        this.yGridLineVisible = false;
        this.xGridBorderVisible = false;
        this.yGridBorderVisible = false;
        this.xGridTickVisible = false;
        this.yGridTickVisible = false;
        this.lineWidth = 0;
        this.lineBorderWidth = 0;

        super.ngOnInit();
        this.updateData();

        this.deckCardSub = this.events.deck.deckCardEvent
            .pipe(debounceTime(500))
            .subscribe({
                next: (event: DeckCardEvent) => {
                    if (event.layer === DeckLayerName.Mainboard) {
                        this.updateData();
                    }
                }
            });
    }

    public ngOnDestroy(): void {
        this.deckCardSub.unsubscribe();
    }

    private updateData(): void {
        var totalLand = 0;
        var totalNonland = 0;
        var total = 0;

        this.allColors.forEach(c => c.reset());
        
        // ---------------------------------------------------------------------------------
        // Compile Data

        this.deckComponent.mainLayer.cards?.forEach(c => {
            var magicCard = c.card.item.data?.magic;
            var count = c.count + c.specialCount;

            if (magicCard) {
                var black = producesBlack(magicCard);
                var white = producesWhite(magicCard);
                var red = producesRed(magicCard);
                var green = producesGreen(magicCard);
                var blue = producesBlue(magicCard);
                var colorless = producesColorless(magicCard);

                if (magicCard.faces![0].typeLine!.indexOf('Land') >= 0) {
                    // Land
                    this.black.landCount += (black ? count : 0);
                    this.white.landCount += (white ? count : 0);
                    this.red.landCount += (red ? count : 0);
                    this.green.landCount += (green ? count : 0);
                    this.blue.landCount += (blue ? count : 0);
                    this.colorless.landCount += (colorless ? count : 0);

                    if (black || white || red || green || blue || colorless) {
                        totalLand += count;
                        total += count;
                    }
                } else {
                    // Nonland
                    this.black.nonlandCount += (black ? count : 0);
                    this.white.nonlandCount += (white ? count : 0);
                    this.red.nonlandCount += (red ? count : 0);
                    this.green.nonlandCount += (green ? count : 0);
                    this.blue.nonlandCount += (blue ? count : 0);
                    this.colorless.nonlandCount += (colorless ? count : 0);

                    if (black || white || red || green || blue || colorless) {
                        totalNonland += count;
                        total += count;
                    }
                }
            }
        });

        this.allColors.forEach(c => { c.calculate(totalLand, totalNonland, total); })

        this.updateDisplay();
    }

    private updateDisplay(): void {
        this.allColors.sort((a, b) => {
            var aCount = (a.landCount * this.landModifier) + (a.nonlandCount * this.nonlandModifier);
            var bCount = (b.landCount * this.landModifier) + (b.nonlandCount * this.nonlandModifier);

            if (aCount == bCount) {
                return (a.tieBreaker > b.tieBreaker) ? -1 : 1;
            } else {
                return (aCount < bCount) ? -1 : 1; 
            }
        });
        
        // ---------------------------------------------------------------------------------
        // Add to chart

        this.resetData();

        this.allColors.forEach(c => {
            var count = (c.landCount * this.landModifier) + (c.nonlandCount * this.nonlandModifier);

            if (count) {
                this.addDataPoint(count, c.label, c.color);
            }
        });

        this.allColors.reverse();
        this.composeSummary();
    }

    private composeSummary(): void {
        this.summary = (this.mode == ProductionDisplayMode.Land ? "No land cards in the deck." : (this.mode == ProductionDisplayMode.Nonland) ? "No nonland mana-producing cards in the deck." : "No mana-producing cards in the deck.");

        var cardDescription = (this.mode == ProductionDisplayMode.Land ? "land cards" : (this.mode == ProductionDisplayMode.Nonland) ? "nonland mana-producing cards" : "all mana-producing cards");

        if (this.allColors[0].getPercentage(this.mode)) {

            if (this.allColors[1].getPercentage(this.mode)) {
                this.summary = `${this.allColors[0].getPercentage(this.mode)}% of ${cardDescription} produce ${this.allColors[0].lowerLabel}`;

                if (this.allColors[2].getPercentage(this.mode)) {
                    for (let i = 1; i < this.allColors.length; ++i) {
                        var currStat = this.allColors[i];

                        if (!currStat.getPercentage(this.mode)) {
                            break;
                        }

                        var doesNextHaveCount = ((i < this.allColors.length - 1) && this.allColors[i + 1].getPercentage(this.mode)) ? true : false;

                        if (doesNextHaveCount) {
                            this.summary += `, ${currStat.getPercentage(this.mode)}% produce ${currStat.lowerLabel}`;
                        } else {
                            this.summary += `, and ${currStat.getPercentage(this.mode)}% produce ${currStat.lowerLabel}.`;
                        }
                    }
                } else {
                    // Only two colors
                    this.summary += ` and ${this.allColors[1].getPercentage(this.mode)}% produce ${this.allColors[1].lowerLabel}.`;
                }
            } else {
                // Only one color.
            this.summary = `${this.allColors[0].getPercentage(this.mode)}% of ${cardDescription} produce ${this.allColors[0].lowerLabel}.`;
            }
        }
    }

    // -------------------------------------------------------------------------------------
    // Mode Status
    // -------------------------------------------------------------------------------------

    public isModeLandActive(): boolean {
        return (this.mode == ProductionDisplayMode.Land);
    }

    public isModeNonlandActive(): boolean {
        return (this.mode == ProductionDisplayMode.Nonland);
    }

    public isModeAllActive(): boolean {
        return (this.mode == ProductionDisplayMode.All);
    }

    // -------------------------------------------------------------------------------------
    // Mode Toggle
    // -------------------------------------------------------------------------------------

    public onModeLandClicked(): void {
        this.setDisplayMode(ProductionDisplayMode.Land);
    }

    public onModeNonlandClicked(): void {
        this.setDisplayMode(ProductionDisplayMode.Nonland);
    }

    public onModeAllClicked(): void {
        this.setDisplayMode(ProductionDisplayMode.All);
    }

    private setDisplayMode(mode: ProductionDisplayMode): void {
        if (mode == this.mode) {
            return;
        }

        this.mode = mode;

        switch (this.mode) {
            case ProductionDisplayMode.All:
                this.landModifier = 1;
                this.nonlandModifier = 1;
                break;

            case ProductionDisplayMode.Nonland:
                this.landModifier = 0;
                this.nonlandModifier = 1;
                break;

            case ProductionDisplayMode.Land:
            default:
                this.landModifier = 1;
                this.nonlandModifier = 0;
                break;
        }

        this.updateDisplay();
    }
}

class ManaProduction {
    public landCount: number = 0;
    public nonlandCount: number = 0;
    public landPercentage: number = 0;
    public nonlandPercentage: number = 0;
    public totalPercentage: number = 0;
    public lowerLabel!: string;

    public constructor(public label: string, public color: string, public tieBreaker: number) {
        this.lowerLabel = label.toLowerCase();
    }

    public reset(): void {
        this.landCount = 0;
        this.nonlandCount = 0;
        this.landPercentage = 0;
        this.nonlandPercentage = 0;
        this.totalPercentage = 0;
    }

    public calculate(totalLand: number, totalNonland: number, total: number): void {
        if (totalLand) {
            this.landPercentage = Math.round((this.landCount / totalLand) * 100);
        }

        if (totalNonland) {
            this.nonlandPercentage = Math.round((this.nonlandCount / totalNonland) * 100);
        }

        if (total) {
            this.totalPercentage = Math.round(((this.landCount + this.nonlandCount) / total) * 100);
        }
    }

    public getPercentage(mode: ProductionDisplayMode): number {
        switch (mode) {
            case ProductionDisplayMode.Nonland:
                return this.nonlandPercentage;

            case ProductionDisplayMode.All:
                return this.totalPercentage;

            case ProductionDisplayMode.Land:
            default:
                return this.landPercentage;
        }
    }
}

enum ProductionDisplayMode {
    Land,
    Nonland,
    All
}